import { isAfter, isBefore } from 'date-fns';

import {
  AirTripTypeEnum, AssistanceByReservationItem, ReservationItems, ReservationType, UserHistoryItem
} from '@user-history/entities/user-history-item';
import { UserHistoryOptions } from '@user-history/entities/user-history-options';
import {
  haveCompatibleReservationsByCrossSelling, getCrossSellingUrlParams, getCrossSellingTranslates,
  getCrossSellingImage, getCrossSellingAction, isValidGeneralCrossSellingByItem,
  isActiveAssistanceCrossSellingListByConfig, isActiveProductListCrossSellingByConfig, getCrossSellingSource
} from '@shared/cross-selling/mappers/cross-selling-general.mapper';
import { CrossSellingReservationItem } from '@shared/cross-selling/entities/cross-selling';

export function getCrossSellingByProductItem(
  item: UserHistoryItem, history: UserHistoryItem[], tab: UserHistoryOptions
): CrossSellingReservationItem {
  const type = item.ReservationItems?.[0].ReservationItemType;
  if (!isActiveProductListCrossSellingByConfig(type)) return null;
  else {
    switch (type) {
      case ReservationType.Flight:
        if (validCrossSellingByFlights(item, history, tab)) {
          return crossSellingObject(item, ReservationType.Hotel);
        } else return null;
      default:
        return null;
    }
  }
}

export function getCrossSellingAssistanceByProductItem(
  item: UserHistoryItem,
  assistance: AssistanceByReservationItem,
  tab: UserHistoryOptions,
): CrossSellingReservationItem {
  if (isValidAssistanceCrossSelling(item, assistance, tab)) {
    return crossSellingObject(item, ReservationType.Assistance);
  } else return null;
}

function isValidAssistanceCrossSelling(
  item: UserHistoryItem,
  assistance: AssistanceByReservationItem,
  tab: UserHistoryOptions
): boolean {
  const reservation = item?.ReservationItems?.[0];
  if (isActiveAssistanceCrossSellingListByConfig(reservation?.ReservationItemType)) {
    const today = new Date();
    const isValidGeneral = isValidGeneralCrossSellingByItem(item, tab);
    const startDate = new Date(reservation?.StartDate);
    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    return isAfter(startDate, today) && isValidGeneral && !assistance?.Company?.Code;
  } else return false;
}

function validCrossSellingByFlights(
  item: UserHistoryItem, history: UserHistoryItem[], tab: UserHistoryOptions
): boolean {
  const reservation = item?.ReservationItems?.[0];
  const isRoundTrip = isRoundTripFlight(reservation);
  const isValidGeneral = isValidGeneralCrossSellingByItem(item, tab);
  const startDate = new Date(reservation.StartDate);
  const endDate = new Date(reservation.EndDate);
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  const haveCompatibleReservation = haveCompatibleReservationsByCrossSelling(
    startDate, endDate, reservation.Destination, history, ReservationType.Hotel);
  return isBefore(startDate, endDate) &&
    (!!isRoundTrip && !!isValidGeneral && !haveCompatibleReservation);
}

function crossSellingObject(item: UserHistoryItem, type: ReservationType): CrossSellingReservationItem {
  const reservation = item?.ReservationItems?.[0];
  return {
    ReservationType: reservation?.ReservationItemType,
    TypeCrossSellingItem: type,
    UrlParams: getCrossSellingUrlParams(type, new Date(reservation.StartDate),
      new Date(reservation.EndDate), reservation.Destination),
    Translations: getCrossSellingTranslates(type),
    Image: getCrossSellingImage(type),
    Action: getCrossSellingAction(type),
    Source: getCrossSellingSource(type),
    AssistanceInfo: null,
    Currency: null,
  };
}

function isRoundTripFlight(reservation: ReservationItems): boolean {
  return reservation.AirTripType === AirTripTypeEnum.RT;
}
